/**
 * SigningEnvelopesPage
 * Landlord view of all signing envelopes with filtering and list display
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { listSigningEnvelopes, cancelEnvelope } from '../../lib/api';
import { PageHeader } from '../../components/layout/index';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { formatDateTime } from '../../utils/date';
import { envelopeStatusLabel } from '../../lib/recipient-status';

interface Envelope {
  id: string;
  title: string;
  status: string;
  sourceType: string;
  sourceId: string;
  createdAt: string;
  sentAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  expiresAt: string | null;
}

interface EnvelopeListResponse {
  envelopes: Envelope[];
}

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'EXPIRED', label: 'Expired' },
];

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'DRAFT': return 'bg-muted text-muted-foreground border-border';
    case 'PENDING': return 'bg-info/15 text-info border-info/25';
    case 'COMPLETED': return 'bg-success/15 text-success border-success/25';
    case 'CANCELLED': return 'bg-destructive/15 text-destructive border-destructive/25';
    case 'EXPIRED': return 'bg-warning/15 text-warning border-warning/25';
    case 'ERROR': return 'bg-destructive/15 text-destructive border-destructive/25';
    default: return 'bg-muted text-muted-foreground border-border';
  }
}


export function SigningEnvelopesPage() {
  const [envelopes, setEnvelopes] = useState<Envelope[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceTypeFilter, setSourceTypeFilter] = useState('');
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const fetchEnvelopes = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const params: { status?: string; sourceType?: string } = {};
      if (statusFilter) params.status = statusFilter;
      if (sourceTypeFilter) params.sourceType = sourceTypeFilter;
      const res = await listSigningEnvelopes(params) as EnvelopeListResponse;
      setEnvelopes(res.envelopes);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load signing envelopes');
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, sourceTypeFilter]);

  useEffect(() => {
    fetchEnvelopes();
  }, [fetchEnvelopes]);

  async function handleCancel(id: string, title: string) {
    if (!window.confirm(`Cancel signing envelope "${title}"? This action cannot be undone.`)) return;
    try {
      setCancellingId(id);
      await cancelEnvelope(id);
      await fetchEnvelopes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel envelope');
    } finally {
      setCancellingId(null);
    }
  }

  return (
    <div
      className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]"
      data-testid="signing-envelopes-page"
    >
      <PageHeader
        title="Signing Envelopes"
        subtitle="Manage document signing requests"
      />

      {/* Filter bar */}
      <div className="flex flex-wrap gap-3 mb-6 bg-card rounded-lg p-4 border border-border">
        <div className="flex flex-col gap-1">
          <label htmlFor="status-filter" className="text-xs font-medium text-muted-foreground">
            Status
          </label>
          <select
            id="status-filter"
            data-testid="signing-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring min-h-[36px]"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="source-type-filter" className="text-xs font-medium text-muted-foreground">
            Source Type
          </label>
          <input
            id="source-type-filter"
            type="text"
            placeholder="e.g. lease"
            value={sourceTypeFilter}
            onChange={(e) => setSourceTypeFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring min-h-[36px]"
          />
        </div>
      </div>

      {error && (
        <div className="flex justify-between items-center px-4 py-3 bg-destructive/10 border border-destructive/25 rounded-lg mb-4">
          <p className="m-0 text-destructive text-sm">{error}</p>
          <Button
            onClick={fetchEnvelopes}
            variant="destructive"
            size="sm"
          >
            Retry
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="bg-card rounded-lg border border-border p-8 space-y-3">
          <Skeleton className="h-8 w-1/3" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-3/4" />
        </div>
      ) : envelopes.length === 0 ? (
        <div
          className="bg-card rounded-lg border border-border p-16 text-center"
          data-testid="signing-empty-state"
        >
          <p className="text-muted-foreground text-sm">No signing envelopes yet.</p>
        </div>
      ) : (
        <div data-testid="signing-envelopes-table">
          {/* Mobile: Card layout */}
          <div className="flex flex-col gap-3 md:hidden">
            {envelopes.map((envelope) => (
              <div
                key={envelope.id}
                data-testid={`signing-envelope-row-${envelope.id}`}
                className="bg-card rounded-lg border border-border p-4"
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <Link
                    to={`/signing/${envelope.id}`}
                    className="text-sm font-medium text-foreground no-underline hover:text-primary flex-1 min-w-0 truncate"
                  >
                    {envelope.title}
                  </Link>
                  <Badge
                    variant="outline"
                    className={`font-semibold shrink-0 ${statusBadgeClass(envelope.status)}`}
                  >
                    {envelopeStatusLabel(envelope.status)}
                  </Badge>
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                  <span>{envelope.sourceType}</span>
                  <span>{formatDateTime(envelope.createdAt, { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                </div>
                <div className="flex gap-2">
                  <Button asChild variant="secondary" size="sm" className="flex-1 min-h-[44px]">
                    <Link to={`/signing/${envelope.id}`}>View</Link>
                  </Button>
                  {(envelope.status === 'DRAFT' || envelope.status === 'PENDING') && (
                    <Button
                      onClick={() => handleCancel(envelope.id, envelope.title)}
                      disabled={cancellingId === envelope.id}
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10 min-h-[44px]"
                    >
                      {cancellingId === envelope.id ? 'Cancelling...' : 'Cancel'}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: Table layout */}
          <div className="hidden md:block bg-card rounded-lg border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/40 border-b border-border">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Title
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Source
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Created
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {envelopes.map((envelope) => (
                    <tr
                      key={envelope.id}
                      data-testid={`signing-envelope-row-${envelope.id}`}
                      className="hover:bg-accent transition-colors"
                    >
                      <td className="px-4 py-3 font-medium text-foreground">
                        {envelope.title}
                      </td>
                      <td className="px-4 py-3">
                        <Badge
                          variant="outline"
                          className={`font-semibold ${statusBadgeClass(envelope.status)}`}
                        >
                          {envelopeStatusLabel(envelope.status)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {envelope.sourceType}: {envelope.sourceId}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {formatDateTime(envelope.createdAt, { year: 'numeric', month: 'short', day: 'numeric' })}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <Button
                            asChild
                            variant="secondary"
                            size="sm"
                          >
                            <Link to={`/signing/${envelope.id}`}>
                              View
                            </Link>
                          </Button>
                          {(envelope.status === 'DRAFT' || envelope.status === 'PENDING') && (
                            <Button
                              onClick={() => handleCancel(envelope.id, envelope.title)}
                              disabled={cancellingId === envelope.id}
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            >
                              {cancellingId === envelope.id ? 'Cancelling...' : 'Cancel'}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SigningEnvelopesPage;
